import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../context/NotificationContext';
import { AdminPermission, AdminRole, AdminRolesService } from '../services/admin-roles.service';
import { useAuth } from '../context/AuthContext';
import styles from './AdminRolesPage.module.css';

const groupOrder = ['nav_admin', 'nav_client', 'nav_developer', 'actions', 'other'];

export default function AdminRolesPage() {
  const { t } = useTranslation();
  const { addNotification } = useNotification();
  const { user, refreshMe } = useAuth();

  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [permissions, setPermissions] = useState<AdminPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const [newRoleCode, setNewRoleCode] = useState('');
  const [newRoleLabel, setNewRoleLabel] = useState('');
  const [newRoleActive, setNewRoleActive] = useState(true);
  const [newRolePermissions, setNewRolePermissions] = useState<string[]>([]);

  const [editLabel, setEditLabel] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [editPermissions, setEditPermissions] = useState<string[]>([]);

  const load = async () => {
    setLoading(true);
    try {
      const [rolesRes, permissionsRes] = await Promise.all([
        AdminRolesService.listRoles(),
        AdminRolesService.listPermissions()
      ]);

      const roleList = (rolesRes as any)?.data || [];
      const permissionList = (permissionsRes as any)?.data || [];

      setRoles(Array.isArray(roleList) ? roleList : []);
      setPermissions(Array.isArray(permissionList) ? permissionList : []);
    } catch (error: any) {
      addNotification(error?.message || t('common.error_generic'), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!roles.length) {
      setSelectedRoleId(null);
      return;
    }

    if (!selectedRoleId || !roles.some(role => role.id === selectedRoleId)) {
      setSelectedRoleId(roles[0].id);
    }
  }, [roles, selectedRoleId]);

  const selectedRole = useMemo(() => roles.find(role => role.id === selectedRoleId) || null, [roles, selectedRoleId]);

  useEffect(() => {
    if (!selectedRole) return;
    setEditLabel(selectedRole.label);
    setEditActive(selectedRole.is_active);
    setEditPermissions(selectedRole.permissions || []);
  }, [selectedRole]);

  const groupedPermissions = useMemo(() => {
    const groups: Record<string, AdminPermission[]> = {};
    permissions.forEach((permission) => {
      const groupKey = permission.group || 'other';
      if (!groups[groupKey]) groups[groupKey] = [];
      groups[groupKey].push(permission);
    });

    Object.values(groups).forEach((list) => list.sort((a, b) => a.label.localeCompare(b.label)));

    return groups;
  }, [permissions]);

  const getGroupLabel = (group: string) => {
    switch (group) {
      case 'nav_admin':
        return t('admin.roles_group_admin');
      case 'nav_client':
        return t('admin.roles_group_client');
      case 'nav_developer':
        return t('admin.roles_group_developer');
      case 'actions':
        return t('admin.roles_group_actions');
      default:
        return group;
    }
  };

  const togglePermission = (setFn: React.Dispatch<React.SetStateAction<string[]>>, code: string) => {
    setFn((prev) => (prev.includes(code) ? prev.filter((item) => item !== code) : [...prev, code]));
  };

  const resetCreateForm = () => {
    setNewRoleCode('');
    setNewRoleLabel('');
    setNewRoleActive(true);
    setNewRolePermissions([]);
  };

  const openCreateModal = () => setIsCreateOpen(true);
  const closeCreateModal = () => {
    setIsCreateOpen(false);
    resetCreateForm();
  };

  const handleCreateRole = async () => {
    if (!newRoleCode.trim() || !newRoleLabel.trim()) {
      addNotification(t('admin.roles_required_fields'), 'warning');
      return;
    }

    setCreating(true);
    try {
      const res = await AdminRolesService.createRole({
        code: newRoleCode.trim(),
        label: newRoleLabel.trim(),
        is_active: newRoleActive,
        permissionCodes: newRolePermissions
      });
      const createdRole = (res as any)?.data as AdminRole | undefined;
      addNotification(t('admin.roles_create_success'), 'success');
      resetCreateForm();
      await load();
      if (createdRole?.id) setSelectedRoleId(createdRole.id);
      setIsCreateOpen(false);
    } catch (error: any) {
      addNotification(error?.message || t('common.error_generic'), 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleSaveRole = async () => {
    if (!selectedRole) return;
    if (!editLabel.trim()) {
      addNotification(t('admin.roles_required_fields'), 'warning');
      return;
    }

    setIsSaving(true);
    try {
      await AdminRolesService.updateRole(selectedRole.id, {
        label: editLabel.trim(),
        is_active: editActive
      });
      await AdminRolesService.updateRolePermissions(selectedRole.id, editPermissions);
      addNotification(t('admin.roles_save_success'), 'success');
      await load();
      if (user?.role && selectedRole?.code && user.role.toLowerCase() === selectedRole.code.toLowerCase()) {
        await refreshMe();
      }
    } catch (error: any) {
      addNotification(error?.message || t('common.error_generic'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const isAdminRole = selectedRole?.code?.toLowerCase() === 'admin';

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>{t('admin.roles_title')}</h1>
          <p className={styles.subtitle}>{t('admin.roles_subtitle')}</p>
        </div>
        <div className={styles.headerActions}>
          <button type="button" className={styles.primaryButton} onClick={openCreateModal}>
            {t('admin.roles_create_action')}
          </button>
        </div>
      </header>

      <div className={styles.contentGrid}>
        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <h3>{t('admin.roles_list_title')}</h3>
          </div>
          {loading ? (
            <div className={styles.loading}>{t('common.loading')}</div>
          ) : (
            <div className={styles.roleList}>
              {roles.map((role) => (
                <button
                  key={role.id}
                  type="button"
                  className={`${styles.roleItem} ${role.id === selectedRoleId ? styles.roleItemActive : ''}`}
                  onClick={() => setSelectedRoleId(role.id)}
                >
                  <div>
                    <div className={styles.roleName}>{role.label}</div>
                    <div className={styles.roleMeta}>{role.code}</div>
                  </div>
                  <div className={`${styles.roleBadge} ${role.is_active ? styles.active : styles.inactive}`}>
                    {role.is_active ? t('status.active') : t('status.inactive')}
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
        <div className={styles.rightColumn}>
          <section className={styles.panel}>
            <div className={styles.panelHeader}>
              <h3>{t('admin.roles_edit_title')}</h3>
            </div>
            {!selectedRole ? (
              <div className={styles.emptyState}>{t('admin.roles_no_role_selected')}</div>
            ) : (
              <div className={styles.formSection}>
                <div className={styles.formRow}>
                  <label className={styles.label}>{t('admin.roles_code_label')}</label>
                  <input className={styles.input} value={selectedRole.code} disabled />
                </div>
                <div className={styles.formRow}>
                  <label className={styles.label}>{t('admin.roles_label_label')}</label>
                  <input
                    className={styles.input}
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                  />
                </div>
                <div className={styles.formRowInline}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={editActive}
                      disabled={isAdminRole}
                      onChange={(e) => setEditActive(e.target.checked)}
                    />
                    {t('admin.roles_active_label')}
                  </label>
                  {selectedRole.userCount !== undefined ? (
                    <span className={styles.muted}>{t('admin.roles_users_count', { count: selectedRole.userCount })}</span>
                  ) : null}
                </div>
                <div className={styles.permissionsSection}>
                  <div className={styles.permissionsHeader}>{t('admin.roles_permissions_title')}</div>
                  {permissions.length === 0 ? (
                    <div className={styles.muted}>{t('admin.roles_permissions_empty')}</div>
                  ) : (
                    groupOrder
                      .filter((groupKey) => groupedPermissions[groupKey]?.length)
                      .map((groupKey) => (
                        <div key={groupKey} className={styles.permissionGroup}>
                          <div className={styles.groupTitle}>{getGroupLabel(groupKey)}</div>
                          <div className={styles.permissionGrid}>
                            {groupedPermissions[groupKey].map((perm) => (
                              <label key={perm.code} className={styles.checkboxLabel}>
                                <input
                                  type="checkbox"
                                  checked={editPermissions.includes(perm.code)}
                                  disabled={!perm.is_active}
                                  onChange={() => togglePermission(setEditPermissions, perm.code)}
                                />
                                <span>{perm.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))
                  )}
                </div>
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={handleSaveRole}
                  disabled={isSaving}
                >
                  {isSaving ? t('common.processing') : t('admin.roles_save_changes')}
                </button>
              </div>
            )}
          </section>
        </div>
      </div>

      {isCreateOpen ? (
        <div className={styles.modalOverlay} onClick={closeCreateModal}>
          <div className={styles.modal} onClick={(event) => event.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>{t('admin.roles_create_title')}</h3>
              <button type="button" className={styles.closeButton} onClick={closeCreateModal}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formSection}>
                <div className={styles.formRow}>
                  <label className={styles.label}>{t('admin.roles_code_label')}</label>
                  <input
                    className={styles.input}
                    value={newRoleCode}
                    onChange={(e) => setNewRoleCode(e.target.value)}
                    placeholder="auditor"
                  />
                </div>
                <div className={styles.formRow}>
                  <label className={styles.label}>{t('admin.roles_label_label')}</label>
                  <input
                    className={styles.input}
                    value={newRoleLabel}
                    onChange={(e) => setNewRoleLabel(e.target.value)}
                    placeholder={t('admin.roles_label_placeholder')}
                  />
                </div>
                <div className={styles.formRowInline}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={newRoleActive}
                      onChange={(e) => setNewRoleActive(e.target.checked)}
                    />
                    {t('admin.roles_active_label')}
                  </label>
                </div>
                <div className={styles.permissionsSection}>
                  <div className={styles.permissionsHeader}>{t('admin.roles_permissions_title')}</div>
                  {permissions.length === 0 ? (
                    <div className={styles.muted}>{t('admin.roles_permissions_empty')}</div>
                  ) : (
                    groupOrder
                      .filter((groupKey) => groupedPermissions[groupKey]?.length)
                      .map((groupKey) => (
                        <div key={groupKey} className={styles.permissionGroup}>
                          <div className={styles.groupTitle}>{getGroupLabel(groupKey)}</div>
                          <div className={styles.permissionGrid}>
                            {groupedPermissions[groupKey].map((perm) => (
                              <label key={perm.code} className={styles.checkboxLabel}>
                                <input
                                  type="checkbox"
                                  checked={newRolePermissions.includes(perm.code)}
                                  disabled={!perm.is_active}
                                  onChange={() => togglePermission(setNewRolePermissions, perm.code)}
                                />
                                <span>{perm.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.ghostButton} onClick={closeCreateModal}>
                {t('common.back')}
              </button>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={handleCreateRole}
                disabled={creating}
              >
                {creating ? t('common.processing') : t('admin.roles_create_action')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
